import type { UserWeightPreset } from "@/lib/actions/weight-presets";
import { toBetLinkSet, withReferral, type BetLinkSet } from "@/lib/bet-links";
import { buildCanonicalFingerprint } from "@/lib/dismissal-fingerprints";
import { gameTimeMatcher } from "@/lib/game-date";
import { marketKey, searchMatcher } from "@/lib/odds-screen/live/derive";
import type { BookId, Catalog, EventHeader, PropsRow, PropsView, RowId } from "@/lib/odds-screen/live/types";
import { promoWithBookTerms } from "./promo-terms";
import { getOfficialPreset, usableWeight, type BookWeights } from "@/lib/weights-config";
import { fairAmerican, oddsInRange } from "@/components/optimizer/odds-range";
import { isFantasyPropsBook } from "@/components/optimizer/props-books";
import type { OptimizerQueryState } from "@/components/optimizer/query-state";
import { isBoostableSlipPlatform, promoBlocksBoost, resolveEffectiveSlipContext } from "@/components/optimizer/props-slip-breakevens";
import { formatSideLabel, matchesOverUnder, outcomeLabel } from "@/lib/selection-side";
import {
  transformOptimizerResults,
  type DisplayPlayerProp,
  type PlatformType,
} from "@/components/optimizer/utils-v2";
import type { SelectionSide } from "@/types/evbook";
import type { ComparisonOddsMap, OptimizerResult, SportsbookLiquidityPair, SportsbookOddsPair } from "@/types/feeds";
import { decimalToAmerican } from "@/utils/calculators/odds-utils";
import { PICK6_BASE_DECIMAL } from "@/components/optimizer/utils-v2/odds";
import { periodCase, playerMarketLabel as cleanMarketDisplay, SPREAD_MARKET } from "@/lib/market-label";
import { slipsStayInOneLeague } from "./slip";
import { WHALES_COLUMN, whalesFair, withoutWhales, type ComparisonColumnId, type RowWhales } from "@/lib/whales";

const LINE_TOLERANCE = 0.001;
const MULTIPLIER_BOOKS: ReadonlySet<string> = new Set(["underdog", "dkpick6"]);

export type Grading = { readonly kind: "graded"; readonly standard: number | null } | { readonly kind: "ungraded" };

export type GradingOf = (book: string) => Grading;

export const UNGRADED: Grading = { kind: "ungraded" };

const FLAT: Grading = { kind: "graded", standard: null };

function fixedStandard(book: string): number | null {
  return slipsStayInOneLeague(book) ? PICK6_BASE_DECIMAL : null;
}

export function catalogGrading(books: Catalog["books"] | undefined): GradingOf {
  const graded = new Map<string, Grading>();
  for (const [book, { standard }] of books ?? []) if (standard !== null) graded.set(book, { kind: "graded", standard });
  return (book) => (MULTIPLIER_BOOKS.has(book) ? graded.get(book) ?? UNGRADED : FLAT);
}

export const NO_STANDARDS: GradingOf = catalogGrading(undefined);

export function standardOf(grading: Grading): number | null {
  return grading.kind === "graded" ? grading.standard : null;
}

export function ranked(x: number | null, y: number | null, sign: number): number {
  if (x === null || y === null) return x === y ? 0 : x === null ? 1 : -1;
  return x < y ? -sign : x > y ? sign : 0;
}

export function legMultiplier(odds: number, standard: number | null): number {
  return standard === null ? 1 : Math.round((odds / standard) * 100) / 100;
}

export function knownToListLeg(book: string, odds: number): boolean {
  if (!MULTIPLIER_BOOKS.has(book)) return true;
  const standard = fixedStandard(book);
  return standard !== null && legMultiplier(odds, standard) === 1;
}

export function slipMultiplier(book: string, odds: number, standard: number | null): number {
  return slipsStayInOneLeague(book) ? legMultiplier(odds, standard) : 1;
}

function sameLine(peerLine: number | null, rowLine: number | null): boolean {
  if (peerLine === null) return true;
  return Math.abs(peerLine - (rowLine ?? 0)) <= LINE_TOLERANCE;
}
export type WeightedFair = { readonly fair: number; readonly ev: number; readonly peers: number };

function devig(odds: number, oppOdds: number | null): number | null {
  if (oppOdds === null) return null;
  const mine = 1 / odds;
  const total = mine + 1 / oppOdds;
  return total > 0 ? mine / total : null;
}

export function weightedFair(row: PropsRow, weights: BookWeights): WeightedFair | null {
  let weightSum = 0;
  let fairSum = 0;
  let peers = 0;
  for (const book of row.books) {
    if (!sameLine(book.line, row.line) || !book.eligible) continue;
    const weight = weights[book.book];
    if (!usableWeight(weight)) continue;
    const fair = book.fair ?? devig(book.odds, book.oppOdds);
    if (fair === null) continue;
    weightSum += weight;
    fairSum += weight * fair;
    peers += 1;
  }
  const whalesWeight = weights[WHALES_COLUMN];
  const whales = usableWeight(whalesWeight) ? whalesFair(row) : null;
  if (whales !== null) {
    weightSum += whalesWeight;
    fairSum += whalesWeight * whales;
    peers += 1;
  }
  if (peers === 0) return null;
  const fair = fairSum / weightSum;
  return { fair, ev: row.odds * fair - 1, peers };
}

export function pricesRequired(row: PropsRow, needed: string): boolean {
  return needed === WHALES_COLUMN ? whalesFair(row) !== null : row.books.some((peer) => peer.book === needed);
}

export type PricedRow = {
  readonly row: PropsRow;
  readonly event: EventHeader;
  readonly book: string;
  readonly grading: Grading;
  readonly weights: BookWeights | null;
};

export function priceDisplay({ row, event, book, grading, weights }: PricedRow, slipTypeId: string | null = null, boostPct = 0): DisplayPlayerProp | null {
  const fair = weights === null ? null : weightedFair(row, weights);
  return weights !== null && fair === null ? null : cachedDisplayRow(row, event, book, fair, slipTypeId, boostPct, grading);
}

export function whalesPriceOn(platform: string): boolean {
  return !isFantasyPropsBook(platform);
}

export function pricesWhales(platform: string, diamond: boolean): boolean {
  return diamond && whalesPriceOn(platform);
}

type PricingChoices = { readonly platform: string; readonly weights: BookWeights | null; readonly requiredBooks: readonly string[]; readonly requiredComparisonBooks: readonly ComparisonColumnId[] };

export function pricedChoices<S extends PricingChoices>(state: S, diamond: boolean): S {
  if (pricesWhales(state.platform, diamond)) return state;
  const weights = state.weights && withoutWhales(state.weights);
  const requiredBooks = state.requiredBooks.filter((book) => book.toLowerCase() !== WHALES_COLUMN);
  const requiredComparisonBooks = state.requiredComparisonBooks.filter((book) => book !== WHALES_COLUMN);
  const unchanged = weights === state.weights && requiredBooks.length === state.requiredBooks.length && requiredComparisonBooks.length === state.requiredComparisonBooks.length;
  return unchanged ? state : { ...state, weights, requiredBooks, requiredComparisonBooks };
}

function weightsKept(state: OptimizerQueryState, userPresets: readonly UserWeightPreset[], keep: (weights: BookWeights) => BookWeights | null): BookWeights | null {
  const custom = state.weights ? keep(state.weights) : null;
  if (custom && Object.keys(custom).length > 0) return custom;
  if (!state.preset) return null;
  const preset = getOfficialPreset(state.preset)?.weights ?? userPresets.find((entry) => entry.name === state.preset)?.weights ?? null;
  return preset === null ? null : keep(preset);
}

export function resolveWeights(state: OptimizerQueryState, userPresets: readonly UserWeightPreset[]): BookWeights | null {
  return weightsKept(state, userPresets, (weights) => weights);
}

export function pricingWeights(state: OptimizerQueryState, userPresets: readonly UserWeightPreset[], diamond: boolean): BookWeights | null {
  return pricesWhales(state.platform, diamond) ? resolveWeights(state, userPresets) : weightsKept(state, userPresets, withoutWhales);
}

export function pricingRequired(state: OptimizerQueryState, diamond: boolean): readonly string[] {
  return [...new Set([...state.requiredBooks, ...state.requiredComparisonBooks].map((value) => value.toLowerCase()).filter((value) => value !== "" && (value !== WHALES_COLUMN || pricesWhales(state.platform, diamond))))];
}


function hashStringToPositiveInt(value: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < value.length; i += 1) {
    hash ^= value.charCodeAt(i);
    hash = (hash * 0x01000193) >>> 0;
  }
  return hash;
}

type Sides = { readonly team: string | null; readonly teamShort: string | null; readonly opponent: string | null; readonly opponentShort: string | null; readonly logo: string | null };

function resolveTeams(row: PropsRow, event: EventHeader): Sides {
  const home = event.home || null;
  const away = event.away || null;
  const homeSide: Sides = { team: home, teamShort: event.homeShort, opponent: away, opponentShort: event.awayShort, logo: event.homeLogoUrl };
  const awaySide: Sides = { team: away, teamShort: event.awayShort, opponent: home, opponentShort: event.homeShort, logo: event.awayLogoUrl };
  const side = row.side.toLowerCase();
  if (row.marketType === "game" && side === "home") return homeSide;
  if (row.marketType === "game" && side === "away") return awaySide;
  if (!row.team) return { team: null, teamShort: null, opponent: home, opponentShort: null, logo: null };
  const raw = row.team.toUpperCase();
  const sameSide = (name: string | null, short: string | null) => (name !== null && raw === name.toUpperCase()) || (short !== null && raw === short.toUpperCase());
  if (sameSide(home, event.homeShort)) return homeSide;
  if (sameSide(away, event.awayShort)) return awaySide;
  return { team: row.team, teamShort: null, opponent: home ?? away, opponentShort: null, logo: null };
}


export function gameSelectionLabel(row: PropsRow, team: string | null, side: SelectionSide): string {
  const subject = team ?? (row.entity.name || row.team);
  const outcome = outcomeLabel(row.side);
  if (outcome !== null) return subject ? `${subject} ${outcome}` : outcome;
  if (side === "HOME" || side === "AWAY") {
    if (row.line !== null && SPREAD_MARKET.test(row.marketKey)) return `${subject} ${row.line === 0 ? "PK" : row.line > 0 ? `+${row.line}` : String(row.line)}`.trim();
    return subject || side;
  }
  if (side === "OVER" || side === "UNDER") {
    const word = side === "OVER" ? "Over" : "Under";
    const line = row.line === null ? "" : ` ${Math.abs(row.line)}`;
    return subject ? `${subject} ${word}${line}` : `${word}${line}`;
  }
  return subject ? `${subject} ${formatSideLabel(side)}` : formatSideLabel(side);
}

const SELECTION_SIDES = new Set<SelectionSide>(["OVER", "UNDER", "HOME", "AWAY", "YES", "NO"]);

function resolveSelectionSide(raw: string): SelectionSide {
  const upper = raw.toUpperCase() as SelectionSide;
  return SELECTION_SIDES.has(upper) ? upper : "OVER";
}

function resolveBetType(side: SelectionSide): "OVER" | "UNDER" {
  return side === "UNDER" || side === "AWAY" || side === "NO" ? "UNDER" : "OVER";
}

function sidedPair<T>(side: "OVER" | "UNDER", mine: T, theirs: T): { over: T; under: T } {
  return side === "OVER" ? { over: mine, under: theirs } : { over: theirs, under: mine };
}

export type DisplayRowOptions = {
  readonly fair?: WeightedFair | null;
  readonly slipTypeId?: string | null;
  readonly boostPct?: number;
  readonly grading: Grading;
};

const UNGRADED_ROW = {
  ev_percentage: null, ev_roi: null, edge_percentage: null, effective_slip_type_id: null, effective_slip_type_label: null,
  effective_slip_breakeven_pct: null, used_implicit_best_slip: false, slip_implied_odds: null,
} as const;

export function profitBoostedEv(fair: number, decimal: number, boostPct: number): number {
  return fair * (1 + (decimal - 1) * (1 + boostPct / 100)) - 1;
}

function boostsProfit(book: string, boostPct: number): boolean {
  return boostPct > 0 && !isBoostableSlipPlatform(book as PlatformType);
}

export function propsRowToDisplayPlayerProp(
  row: PropsRow,
  event: EventHeader,
  book: string,
  options: DisplayRowOptions,
): DisplayPlayerProp | null {
  const game = row.marketType === "game";
  const selectionSide = resolveSelectionSide(row.side);
  const side = resolveBetType(selectionSide);
  const line = row.line ?? 0;
  const fair = options.fair ? options.fair.fair : row.fair;
  const boostPct = options.boostPct ?? 0;
  const ev = boostsProfit(book, boostPct) ? profitBoostedEv(fair, row.odds, boostPct) : options.fair ? options.fair.ev : row.ev;
  const peers = options.fair ? options.fair.peers : row.peers;
  const american = decimalToAmerican(row.odds);
  const oppAmerican = row.oppOdds === null ? null : decimalToAmerican(row.oppOdds);
  const statType = row.marketKey || marketKey(row.market) || "unknown_market";
  const startIso = new Date(event.startMs).toISOString();
  const { team, teamShort, opponent, opponentShort, logo } = resolveTeams(row, event);
  const sport = row.league.toUpperCase();

  const sportsbookOdds: Record<string, SportsbookOddsPair> = { [book]: sidedPair(side, american, oppAmerican) };
  const comparisonOdds: ComparisonOddsMap = {};
  const links: Record<string, BetLinkSet> = {};
  const selectionIds: Record<string, string> = {};
  const liquidity: Record<string, SportsbookLiquidityPair> = {};
  const activeLinks = toBetLinkSet(row.links[0], row.links[1], row.links[2]);
  if (activeLinks) links[book] = activeLinks;
  if (row.selectionId) selectionIds[book] = row.selectionId;
  liquidity[book] = sidedPair(side, row.limit, row.oppLimit);
  for (const peer of row.books) {
    if (peer.book === book) continue;
    const peerAmerican = decimalToAmerican(peer.odds);
    const peerOpp = peer.oppOdds === null ? null : decimalToAmerican(peer.oppOdds);
    sportsbookOdds[peer.book] = { ...sidedPair(side, peerAmerican, peerOpp), line: peer.line };
    comparisonOdds[peer.book] = { american_odds: peerAmerican, selection_side: side, line: peer.line };
    const set = toBetLinkSet(peer.links[0], peer.links[1], peer.links[2]);
    if (set) links[peer.book] = set;
    if (peer.selectionId) selectionIds[peer.book] = peer.selectionId;
    liquidity[peer.book] = sidedPair(side, peer.limit, peer.oppLimit);
  }

  const result: OptimizerResult = {
    id: `props:${row.id}`,
    sport,
    league: sport,
    game_id: hashStringToPositiveInt(event.id),
    player_id: hashStringToPositiveInt(row.entity.key || `${event.id}:${row.side}:${statType}`),
    player_name: game ? event.name : row.entity.name,
    team,
    opponent,
    game_start_time: startIso,
    event: event.name,
    event_short: event.awayShort !== null && event.homeShort !== null ? `${event.awayShort} @ ${event.homeShort}` : null,
    team_short: teamShort,
    opponent_short: opponentShort,
    player_position: game ? null : row.player?.position ?? null,
    player_image_url: game ? logo : row.player?.imageUrl ?? null,
    event_start: startIso,
    event_id: event.id,
    entity_key: row.entity.key,
    selection_id: row.selectionId || null,
    opp_selection_id: row.oppSelectionId || null,
    stat_type: statType,
    market: row.market,
    market_type: game ? "game" : "player",
    market_display: game ? row.market : cleanMarketDisplay(row.market),
    line,
    over_under: side,
    selection: game ? gameSelectionLabel(row, team, selectionSide) : selectionSide,
    selection_side: selectionSide,
    selectionSide,
    fantasy_sportsbook: book,
    fantasy_american_odds: american,
    fantasy_implied_probability: 1 / row.odds,
    opp_american_odds: oppAmerican,
    sportsbook: book,
    sportsbook_american_odds: american,
    consensus_implied_probability: fair,
    fair_implied_probability: fair,
    ev_percentage: ev * 100,
    ev_roi: ev,
    odds_to_hit_percentage: fair * 100,
    sportsbook_odds: sportsbookOdds,
    comparison_odds: Object.keys(comparisonOdds).length > 0 ? comparisonOdds : undefined,
    comparison_bet_links: Object.keys(links).length > 0 ? links : undefined,
    comparison_selection_ids: Object.keys(selectionIds).length > 0 ? selectionIds : undefined,
    sportsbook_liquidity: liquidity,
    created_at: startIso,
    updated_at: startIso,
    processing_batch: startIso,
    peer_book_count: peers,
    bet_link: row.links[0] ? withReferral(row.links[0]) : null,
  };

  const { grading } = options;
  const standard = standardOf(grading);
  const display = transformOptimizerResults([result], book, { platform: book as PlatformType, slipTypeId: options.slipTypeId ?? null, boostPct: options.boostPct ?? 0, standardDecimal: standard, league: row.league })[0];
  if (display === undefined) return null;
  return { ...display, ...(grading.kind === "ungraded" && UNGRADED_ROW), promo: promoWithBookTerms(book, row.promo), payout_multiplier: grading.kind === "graded" ? slipMultiplier(book, row.odds, grading.standard) : undefined, grading, book_pick_id: row.pick, outcome_key: game ? row.side : null, whales: rowWhales(row, book) };
}

function rowWhales(row: PropsRow, book: string): RowWhales | null {
  if (row.cell === null || row.whales === null) return null;
  return { topic: { kind: "whales", league: row.league, event: row.eventId, key: row.cell }, side: row.side.toLowerCase(), summary: row.whales, book };
}

type CachedDisplay = { readonly key: string; readonly display: DisplayPlayerProp | null };
const displayCache = new WeakMap<PropsRow, CachedDisplay>();

function cachedDisplayRow(
  row: PropsRow,
  event: EventHeader,
  book: string,
  fair: WeightedFair | null,
  slipTypeId: string | null,
  boostPct: number,
  grading: Grading,
): DisplayPlayerProp | null {
  const key = [
    event.id, event.startMs, event.name, event.home, event.away, event.homeShort ?? "", event.awayShort ?? "", event.homeLogoUrl ?? "", event.awayLogoUrl ?? "",
    row.league, book, slipTypeId ?? "", boostPct, grading.kind === "graded" ? grading.standard ?? "" : grading.kind,
    fair === null ? "" : `${fair.fair}:${fair.ev}:${fair.peers}`,
  ].join("|");
  const hit = displayCache.get(row);
  if (hit !== undefined && hit.key === key) return hit.display;
  const display = propsRowToDisplayPlayerProp(row, event, book, { fair, slipTypeId, boostPct, grading });
  displayCache.set(row, { key, display });
  return display;
}

const MATCHUP_SPLIT = /\s+(?:VS|V\.?|@|AT)\s+/i;

export function canonicalMatchup(value: string): string {
  const normalized = value.trim().replace(/\s+/g, " ");
  const parts = normalized.split(MATCHUP_SPLIT);
  if (parts.length === 2) {
    const [left, right] = parts.map((part) => part.trim().toUpperCase());
    if (left && right) return [left, right].sort().join("::");
  }
  return normalized.toUpperCase();
}

const marketOrder = new Intl.Collator("en", { sensitivity: "base", numeric: true });

function marketChoices(raw: ReadonlySet<string>): string[] {
  return [...new Set([...raw].map(periodCase))].sort(marketOrder.compare);
}

export type FoldInput = {
  readonly view: PropsView;
  readonly state: OptimizerQueryState;
  readonly book: string;
  readonly userPresets: readonly UserWeightPreset[];
  readonly hidden: ReadonlySet<string>;
  readonly purpose?: "board" | "slips";
  readonly nowMs?: number;
  readonly gradingOf: GradingOf;
  readonly diamond?: boolean;
};

export type DisplayResolver = (id: RowId) => DisplayPlayerProp | null;

export type RowSource = {
  readonly ids: readonly RowId[];
  readonly total: number;
  readonly markets: readonly string[];
  readonly gameMarkets: readonly string[];
  readonly matchups: readonly string[];
  readonly hitOf: (id: RowId) => number | undefined;
  readonly rowAt: DisplayResolver;
  readonly pricedAt: (id: RowId) => PricedRow | null;
};

export const NO_ROW_SOURCE: RowSource = {
  ids: [],
  total: 0,
  markets: [],
  gameMarkets: [],
  matchups: [],
  hitOf: () => undefined,
  rowAt: () => null,
  pricedAt: () => null,
};

export function createPeerBooks(): (rows: PropsView["rows"]) => readonly BookId[] {
  let seen: PropsView["rows"] = new Map();
  const counts = new Map<BookId, number>();
  let books: readonly BookId[] = [];
  let changed = false;
  const count = (row: PropsRow, delta: number) => {
    for (const peer of row.books) {
      const next = (counts.get(peer.book) ?? 0) + delta;
      if (next > 0) {
        if (!counts.has(peer.book)) changed = true;
        counts.set(peer.book, next);
      } else {
        counts.delete(peer.book);
        changed = true;
      }
    }
  };
  return (rows) => {
    if (rows === seen) return books;
    let matched = 0;
    for (const [id, row] of rows) {
      const held = seen.get(id);
      if (held !== undefined) matched += 1;
      if (held === row) continue;
      if (held !== undefined) count(held, -1);
      count(row, 1);
    }
    if (matched < seen.size) for (const [id, row] of seen) if (!rows.has(id)) count(row, -1);
    seen = rows;
    if (changed) books = [...counts.keys()].sort();
    changed = false;
    return books;
  };
}

export function displayRow(
  row: PropsRow,
  event: EventHeader,
  book: string,
  state: OptimizerQueryState,
  userPresets: readonly UserWeightPreset[],
  grading: Grading,
  diamond: boolean,
): DisplayPlayerProp | null {
  return priceDisplay({ row, event, book, grading, weights: pricingWeights(state, userPresets, diamond) }, state.slipType || null, row.promo !== null && promoBlocksBoost(book as PlatformType) ? 0 : state.boost);
}

export function displayResolver(view: PropsView, state: OptimizerQueryState, book: string, userPresets: readonly UserWeightPreset[], grading: Grading, diamond: boolean): DisplayResolver {
  return (id) => {
    const row = view.rows.get(id);
    const event = row === undefined ? undefined : view.events.get(row.eventId);
    if (row === undefined || event === undefined) return null;
    return displayRow(row, event, book, state, userPresets, grading, diamond);
  };
}

export function rawFingerprint(row: PropsRow, event: EventHeader): string | null {
  return buildCanonicalFingerprint({
    sport: row.league.toUpperCase(),
    eventId: hashStringToPositiveInt(event.id),
    entityId: row.marketType === "game" ? event.name : row.entity.name,
    market: row.marketKey || marketKey(row.market) || "unknown_market",
    line: row.line ?? 0,
    side: resolveSelectionSide(row.side),
  });
}

type Kept = {
  readonly id: RowId;
  readonly row: PropsRow;
  readonly event: EventHeader;
  readonly hit: number;
  readonly ev: number | null;
  readonly american: number;
  readonly boost: number;
};

type BreakevenFor = (american: number, league: string, promo: boolean) => number | null;

const NUMERIC_KEYS: Record<string, (kept: Kept, breakevenFor: BreakevenFor) => number | null> = {
  ev_percentage: (kept) => (kept.ev === null ? null : kept.ev * 100),
  ev_roi: (kept) => kept.ev,
  kelly_fraction: (kept) => kept.ev,
  odds_to_hit: (kept) => kept.hit,
  consensus_implied_probability: (kept) => kept.hit,
  line: (kept) => kept.row.line ?? 0,
  primary_odds: (kept) => kept.american,
  edge_percentage: (kept, breakevenFor) => {
    const breakeven = breakevenFor(kept.american, kept.row.league, kept.row.promo !== null);
    return breakeven === null ? null : kept.hit * kept.boost - breakeven;
  },
  effective_slip_breakeven_pct: (kept, breakevenFor) => breakevenFor(kept.american, kept.row.league, kept.row.promo !== null),
};

const TEXT_KEYS: Record<string, (kept: Kept) => string> = {
  player_name: (kept) => kept.row.entity.name,
  selection_label: (kept) => kept.row.entity.name,
  stat_type: (kept) => kept.row.marketKey || marketKey(kept.row.market),
  bet_type: (kept) => kept.row.side,
  team: (kept) => kept.row.team,
  market_display: (kept) => cleanMarketDisplay(kept.row.market),
  event_name: (kept) => kept.event.name,
  league: (kept) => kept.row.league,
};

function compareKept(column: string, direction: "asc" | "desc", breakevenFor: BreakevenFor, best: (kept: Kept) => number | null): (a: Kept, b: Kept) => number {
  const sign = direction === "asc" ? 1 : -1;
  if (column === "game_start_timestamp") return (a, b) => sign * (a.event.startMs - b.event.startMs) || ranked(best(a), best(b), -1);
  const text = TEXT_KEYS[column];
  if (text) {
    return (a, b) => {
      const x = text(a).toLowerCase();
      const y = text(b).toLowerCase();
      return x < y ? -sign : x > y ? sign : 0;
    };
  }
  const numeric = NUMERIC_KEYS[column] ?? NUMERIC_KEYS.ev_percentage;
  return (a, b) => ranked(numeric(a, breakevenFor), numeric(b, breakevenFor), sign);
}

type Fold = {
  readonly view: PropsView;
  readonly state: OptimizerQueryState;
  readonly book: string;
  readonly userPresets: readonly UserWeightPreset[];
  readonly hidden: ReadonlySet<string>;
  readonly purpose: "board" | "slips";
  readonly nowMs: number;
  readonly grading: Grading;
  readonly diamond: boolean;
  readonly kept: readonly Kept[];
  readonly position: (id: RowId) => number;
  readonly source: RowSource;
};

function positionIn(rowIds: readonly RowId[]): (id: RowId) => number {
  let positions: Map<RowId, number> | null = null;
  return (id) => {
    if (positions === null) {
      positions = new Map();
      for (let at = 0; at < rowIds.length; at += 1) positions.set(rowIds[at], at);
    }
    return positions.get(id) ?? -1;
  };
}

function sameIds(left: readonly RowId[], right: readonly RowId[]): boolean {
  if (left === right) return true;
  if (left.length !== right.length) return false;
  for (let index = 0; index < left.length; index += 1) if (left[index] !== right[index]) return false;
  return true;
}

function movedEvents(previous: PropsView["events"], next: PropsView["events"]): ReadonlySet<string> | null {
  if (previous === next) return null;
  const moved = new Set<string>();
  for (const [id, event] of next) if (previous.get(id) !== event) moved.add(id);
  for (const id of previous.keys()) if (!next.has(id)) moved.add(id);
  return moved.size === 0 ? null : moved;
}

function mergeKept(left: readonly Kept[], right: readonly Kept[], order: (a: Kept, b: Kept) => number): readonly Kept[] {
  if (right.length === 0) return left;
  const out: Kept[] = new Array(left.length + right.length);
  let i = 0;
  let j = 0;
  let k = 0;
  while (i < left.length && j < right.length) out[k++] = order(left[i], right[j]) <= 0 ? left[i++] : right[j++];
  while (i < left.length) out[k++] = left[i++];
  while (j < right.length) out[k++] = right[j++];
  return out;
}

function keptIds(kept: readonly Kept[], previous: readonly RowId[] | undefined): readonly RowId[] {
  if (previous !== undefined && previous.length === kept.length && kept.every((entry, index) => entry.id === previous[index])) return previous;
  return kept.map((entry) => entry.id);
}

type Choices = Pick<RowSource, "markets" | "gameMarkets" | "matchups">;

type Choice = { readonly game: boolean; readonly market: string; readonly eventId: string };

function choiceOf(row: PropsRow): Choice {
  return { game: row.marketType === "game", market: row.market, eventId: row.eventId };
}

function sameChoice(left: PropsRow, right: PropsRow): boolean {
  const a = choiceOf(left);
  const b = choiceOf(right);
  return (Object.keys(a) as (keyof Choice)[]).every((key) => a[key] === b[key]);
}

type Carried = {
  readonly kept: readonly Kept[];
  readonly position: (id: RowId) => number;
  readonly choices: Choices | null;
};

function carry(
  prior: Fold,
  view: PropsView,
  keep: (id: RowId, row: PropsRow, event: EventHeader) => Kept | null,
  compare: (a: Kept, b: Kept) => number,
): Carried {
  const old = prior.view;
  const moved = movedEvents(old.events, view.events);
  const sameOrder = sameIds(old.rowIds, view.rowIds);
  const position = sameOrder ? prior.position : positionIn(view.rowIds);
  const order = (a: Kept, b: Kept) => compare(a, b) || position(a.id) - position(b.id);
  let choicesHold = sameOrder && moved === null;
  const fresh: Kept[] = [];
  for (const id of view.rowIds) {
    const row = view.rows.get(id);
    const held = old.rows.get(id);
    if (row === undefined || (row === held && !moved?.has(row.eventId))) continue;
    if (held === undefined || !sameChoice(row, held)) choicesHold = false;
    const event = view.events.get(row.eventId);
    const entry = event === undefined ? null : keep(id, row, event);
    if (entry !== null) fresh.push(entry);
  }
  const carried = prior.kept.filter((entry) => view.rows.get(entry.id) === entry.row && !moved?.has(entry.row.eventId));
  const inOrder = sameOrder || carried.every((entry, index) => index === 0 || order(carried[index - 1], entry) <= 0);
  return {
    kept: mergeKept(inOrder ? carried : carried.sort(order), fresh.sort(order), order),
    position,
    choices: choicesHold ? prior.source : null,
  };
}

export function foldRows(input: FoldInput): RowSource {
  return foldFrom(input, null).source;
}

export function createFold(): (input: Omit<FoldInput, "book"> & { readonly book: string | null }) => RowSource {
  let last: Fold | null = null;
  return ({ book, ...input }) => {
    if (book === null) return NO_ROW_SOURCE;
    last = foldFrom({ ...input, book }, last);
    return last.source;
  };
}

function foldFrom({ view, state, book, userPresets, hidden, purpose = "board", nowMs = Date.now(), gradingOf, diamond = false }: FoldInput, prior: Fold | null): Fold {
  const grading = gradingOf(book);
  const graded = grading.kind === "graded";
  const standard = standardOf(grading);
  const profitBoost = boostsProfit(book, state.boost);
  const weights = pricingWeights(state, userPresets, diamond);
  const statTypes = new Set(state.statTypes.map((value) => value.toLowerCase()));
  const matchups = new Set(state.matchups.map(canonicalMatchup));
  const matches = purpose === "board" ? searchMatcher(state.playerSearch) : null;
  const required = pricingRequired(state, diamond).filter((value) => value !== book);
  const minPeers = weights ? Math.max(1, state.minDataPoints) : state.minDataPoints;
  const rangeActive = state.minOddsToHit > 0 || state.maxOddsToHit < 100;
  const fairPriced = isFantasyPropsBook(book);
  const startsIn = gameTimeMatcher({ days: state.gameDays, within: state.startsWithin, live: false }, new Date(nowMs));
  const slipTypeId = state.slipType || null;
  const breakevens = new Map<string, number | null>();
  const promoUnboosted = promoBlocksBoost(book as PlatformType);
  const breakevenFor: BreakevenFor = (american, league, promo) => {
    if (!graded) return null;
    const unboosted = promo && promoUnboosted;
    const key = `${league}|${american}|${unboosted}`;
    const held = breakevens.get(key);
    if (held !== undefined) return held;
    const value = resolveEffectiveSlipContext({ platform: book as PlatformType, rawSlipTypeId: slipTypeId, americanOdds: american, boostPct: unboosted ? 0 : state.boost, standardDecimal: standard, league }).effectiveBreakevenPct;
    breakevens.set(key, value);
    return value;
  };
  const keep = (id: RowId, row: PropsRow, event: EventHeader): Kept | null => {
    if (!graded && purpose === "slips") return null;
    if (state.marketScope && row.marketType !== state.marketScope) return null;
    if (startsIn !== null && !startsIn(event.startMs)) return null;
    if (statTypes.size > 0 && !statTypes.has(row.market.toLowerCase()) && !statTypes.has(row.marketKey.toLowerCase())) return null;
    if (matchups.size > 0 && !matchups.has(canonicalMatchup(event.name))) return null;
    if (state.hidePushProps && row.marketType === "player" && row.line !== null && Number.isInteger(row.line)) return null;
    if (!matchesOverUnder(row.side, state.overUnder)) return null;
    if (matches && !matches(row.entity.name, row.team, row.market, event.name)) return null;
    if (required.length > 0 && !required.every((needed) => pricesRequired(row, needed))) return null;
    const fair = weights ? weightedFair(row, weights) : null;
    if (weights && fair === null) return null;
    if ((fair ? fair.peers : row.peers) < minPeers) return null;
    if (row.limit !== null && row.limit > 0 && row.limit < state.minLiquidity) return null;
    const fairProb = fair ? fair.fair : row.fair;
    const ev = !graded ? null : profitBoost ? profitBoostedEv(fairProb, row.odds, state.boost) : fair ? fair.ev : row.ev;
    if (state.minEV > 0 && (ev === null || ev * 100 < state.minEV)) return null;
    const hit = fairProb * 100;
    if (rangeActive && (hit < state.minOddsToHit || hit > state.maxOddsToHit)) return null;
    const american = decimalToAmerican(row.odds);
    if (!oddsInRange(fairPriced ? fairAmerican(hit) : american, state)) return null;
    if (row.marketType === "player" && row.promo === null && legMultiplier(row.odds, standard) !== 1) return null;
    const boost = row.promo === null ? 1 : slipMultiplier(book, row.odds, standard);
    if (hidden.size > 0) {
      const fingerprint = rawFingerprint(row, event);
      if (fingerprint && hidden.has(fingerprint)) return null;
    }
    return { id, row, event, hit, ev, american, boost };
  };
  const compare = purpose === "board" ? compareKept(state.sort, state.order ?? "desc", breakevenFor, fairPriced ? (entry) => entry.hit : (entry) => entry.ev) : null;
  const markets = new Set<string>();
  const gameMarkets = new Set<string>();
  const matchupOptions: string[] = [];
  const seenEvents = new Set<string>();
  const note = ({ game, market, eventId }: Choice, event: EventHeader) => {
    (game ? gameMarkets : markets).add(market);
    if (!seenEvents.has(eventId)) {
      seenEvents.add(eventId);
      matchupOptions.push(event.name);
    }
  };
  const reusable = (fold: Fold) => fold.purpose === purpose && fold.state === state && fold.book === book && fold.userPresets === userPresets
    && fold.hidden === hidden && fold.nowMs === nowMs && fold.grading === grading && fold.diamond === diamond
    && fold.view.rows.size === fold.view.rowIds.length && view.rows.size === view.rowIds.length;
  const carried = compare !== null && prior !== null && reusable(prior) ? carry(prior, view, keep, compare) : null;
  let kept: readonly Kept[];

  if (carried === null) {
    const all: Kept[] = [];
    for (const id of view.rowIds) {
      const row = view.rows.get(id);
      const event = row === undefined ? undefined : view.events.get(row.eventId);
      if (row === undefined || event === undefined) continue;
      note(choiceOf(row), event);
      const entry = keep(id, row, event);
      if (entry !== null) all.push(entry);
    }
    kept = compare === null ? all : all.sort(compare);
  } else {
    kept = carried.kept;
    if (carried.choices === null) {
      for (const id of view.rowIds) {
        const row = view.rows.get(id);
        const event = row === undefined ? undefined : view.events.get(row.eventId);
        if (row !== undefined && event !== undefined) note(choiceOf(row), event);
      }
    }
  }

  let hits: Map<RowId, number> | null = null;
  const choices = carried?.choices ?? { markets: marketChoices(markets), gameMarkets: marketChoices(gameMarkets), matchups: matchupOptions };
  const source: RowSource = {
    ids: keptIds(kept, prior?.source.ids),
    total: view.rowIds.length,
    markets: choices.markets,
    gameMarkets: choices.gameMarkets,
    matchups: choices.matchups,
    hitOf: (id) => (hits ??= new Map(kept.map((entry) => [entry.id, entry.hit]))).get(id),
    rowAt: displayResolver(view, state, book, userPresets, grading, diamond),
    pricedAt: (id) => {
      const row = view.rows.get(id);
      const event = row === undefined ? undefined : view.events.get(row.eventId);
      return row === undefined || event === undefined ? null : { row, event, book, grading, weights };
    },
  };
  return { view, state, book, userPresets, hidden, purpose, nowMs, grading, diamond, kept, position: carried?.position ?? positionIn(view.rowIds), source };
}

export function displayRowsFor({
  view, state, book, userPresets, rowIds, gradingOf, diamond = false,
}: Omit<FoldInput, "hidden" | "purpose"> & { readonly rowIds: readonly string[] }): ReadonlyMap<string, DisplayPlayerProp> {
  const resolve = displayResolver(view, state, book, userPresets, gradingOf(book), diamond);
  const out = new Map<string, DisplayPlayerProp>();
  for (const id of rowIds) {
    const display = resolve(id);
    if (display !== null) out.set(id, display);
  }
  return out;
}
